import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Container,
  Stack,
  useTheme,
  useMediaQuery,
  Grid,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { authenticatedFetch } from "../../utils/api";
import { usePermissions } from "../../utils/permissions";
import { useTranslation } from "../../i18n";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

interface InvoiceItem {
  id: number;
  rate: string;
  weight: string;
  amount: string;
}

interface Invoice {
  id: number;
  invoice_number: string;
  invoice_date: string;
  particulars: string;
  total_weight: string;
  subtotal: string;
  cgst_percentage: string;
  sgst_percentage: string;
  cgst_amount: string;
  sgst_amount: string;
  total_amount: string;
  company: {
    id: number;
    name: string;
  };
  invoice_items: InvoiceItem[];
  dc_numbers_display: string;
  created_at: string;
}

export default function InvoiceList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const { translations: t } = useTranslation();
  const { canCreate } = usePermissions();

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      const response = await authenticatedFetch(`${API_URL}/invoices`);
      if (response.status === 401) {
        navigate("/login");
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch invoices");
      }
      const data = await response.json();
      setInvoices(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(num);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Card sx={{ mt: { xs: 2, sm: 3, md: 4 } }}>
        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              mb: 3,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "stretch", sm: "center" },
              gap: 2,
            }}
          >
            <Typography variant={isMobile ? "h6" : "h5"} component="h2">
              {t.invoices.title}
            </Typography>
            {canCreate('invoices') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/invoices/new")}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                {t.invoices.createNew}
              </Button>
            )}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {invoices.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>
              {t.invoices.noInvoices}
            </Typography>
          ) : isMobile ? (
            <Stack spacing={2}>
              {invoices.map((invoice) => (
                <Card
                  key={invoice.id}
                  variant="outlined"
                  onClick={() => navigate(`/invoices/${invoice.id}`)}
                  sx={{
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      boxShadow: 2,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color="primary"
                        fontWeight="bold"
                      >
                        Invoice #{invoice.invoice_number}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(invoice.invoice_date).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      {invoice.company.name}
                    </Typography>

                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          DC Nos
                        </Typography>
                        <Typography variant="body2">
                          {invoice.dc_numbers_display}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          Weight
                        </Typography>
                        <Typography variant="body2">
                          {parseFloat(invoice.total_weight).toFixed(2)} kg
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Total Amount
                        </Typography>
                        <Typography variant="h6" color="primary" fontWeight="bold">
                          {formatCurrency(invoice.total_amount)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice No</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>DC Nos</TableCell>
                    <TableCell align="right">Weight (kg)</TableCell>
                    <TableCell align="right">Subtotal</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      hover
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Chip
                          label={invoice.invoice_number}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(invoice.invoice_date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>{invoice.company.name}</TableCell>
                      <TableCell>{invoice.dc_numbers_display}</TableCell>
                      <TableCell align="right">
                        {parseFloat(invoice.total_weight).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(invoice.subtotal)}
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontWeight="bold">
                          {formatCurrency(invoice.total_amount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

